/**
 * Шапка колонок — полоса 26 px, sticky (§4.2, §10.3, §4.9).
 *
 * Клик по заголовку: asc → desc → снять, Shift+клик добавляет уровень,
 * в заголовке появляется бейдж ¹ ² ³ со стрелкой. Колонка зоны
 * ответственности подчёркнута 2 px `--accent` и объясняет себя тултипом.
 * Канал-зависимые колонки несут подпись канала: «URL (РОЗНИЦА)» (§4.6).
 */
import { useCallback, useRef, useState } from 'react'
import type { SortRule } from '@shared/types.ts'
import {
  Checkbox, IconArrowDown, IconArrowUp, IconButton, IconCheck, IconEyeOff,
  IconSettings, Menu, Tooltip, useDragSort,
  type MenuItem,
} from '../../../components/index.js'
import { ColumnSettings } from './ColumnSettings.js'
import { tableTexts as t } from '../texts.table.js'
import { FIXED_HEAD, type ColumnDef } from './columns.js'
import { setColumnOrderIn } from './columnOrder.js'
import { moveColumnToGroup } from './columnMove.js'
import './TableHeader.css'

export interface TableHeaderProps {
  columns: ColumnDef[]
  leftOffset: ReadonlyMap<string, number>
  rightOffset: ReadonlyMap<string, number>
  lastLeftCode: string | null
  firstRightCode: string | null
  totalWidth: number
  /** Имя активного канала для подписи канал-зависимых колонок. */
  channelName: string
  /** Колонка зоны ответственности: подчёркивание и подпись «ваше поле». */
  myColumn: string | null
  sort: readonly SortRule[]
  onSort: (code: string, additive: boolean) => void
  /** Чекбокс «Выделить все N показанных» (§5.7). */
  shownCount: number
  allSelected: boolean
  someSelected: boolean
  onToggleAll: (checked: boolean) => void
  selectionEnabled: boolean
  /** Пункты меню колонки: закрепить слева, скрыть, развернуть все. */
  /** Явный выбор сортировки из меню колонки: направление или сброс. */
  onSortDirection: (code: string, direction: 'asc' | 'desc' | null) => void
  onResizeColumn: (code: string, width: number) => void
  onHide: (code: string) => void
  onExpandAll: () => void
  pinnedLeftExtra: string | null
}

export function TableHeader(props: TableHeaderProps) {
  const {
    columns, leftOffset, rightOffset, lastLeftCode, firstRightCode, totalWidth,
    channelName, myColumn, sort, onSort, shownCount, allSelected, someSelected,
    onToggleAll, selectionEnabled, onSortDirection, onResizeColumn, onHide, onExpandAll,
  } = props

  const [menu, setMenu] = useState<{ code: string; anchor: HTMLElement } | null>(null)
  const [settings, setSettings] = useState<{ code: string; anchor: HTMLElement } | null>(null)
  const rowRef = useRef<HTMLDivElement | null>(null)

  /**
   * Колонку переставляют прямо в шапке: взял заголовок, подержал — и он
   * поехал, а соседи расступились. Отпустили рядом с чужим блоком — колонка
   * туда и переедет: ровно это и значит «перенести колонку в другой блок».
   */
  const applyOrder = useCallback((order: string[], code: string) => {
    const byCode = new Map(columns.map((column) => [column.code, column]))
    const me = byCode.get(code)
    if (!me) return
    const index = order.indexOf(code)
    const before = order.slice(0, index).reverse()
      .map((item) => byCode.get(item)).find((column) => column?.optional)
    const after = order.slice(index + 1)
      .map((item) => byCode.get(item)).find((column) => column?.optional)
    /* В другой блок колонка переезжает, только если её положили внутрь него —
       между двумя его колонками. На стыке блоков это всего лишь перестановка:
       иначе колонка меняла бы блок от неточного движения мышью. */
    const group = before && after
      ? (before.group === after.group ? before.group : me.group)
      : (before?.group ?? after?.group ?? me.group)

    setColumnOrderIn(group, order.filter((item) => (
      item === code || byCode.get(item)?.group === group
    )))
    if (group !== me.group) void moveColumnToGroup(code, group)
  }, [columns])

  const drag = useDragSort({
    axis: 'x',
    items: columns.map((column) => column.code),
    /* Закреплённая слева зона — чекбокс и имя бренда — остаётся на месте. */
    isFixed: (code) => (FIXED_HEAD as readonly string[]).includes(code)
      || columns.find((column) => column.code === code)?.role === 'gutter',
    /* Заголовок сам по себе кнопка сортировки, поэтому запрет точечный. */
    ignoreSelector: '[data-no-drag]',
    scroller: () => rowRef.current?.closest('.table__scroll') as HTMLElement | null,
    onDrop: applyOrder,
  })

  const sortIndex = (code: string) => sort.findIndex((rule) => rule.column === code)

  return (
    <div className="col-head" role="row" ref={rowRef} style={{ width: `${totalWidth}px` }}>
      <span className="head-plate head-plate--bottom" aria-hidden="true" />
      {/* Контур и тень — поверх закреплённых колонок, иначе их фон съедает
          линию слева. */}
      <span className="head-plate head-plate--bottom-frame" aria-hidden="true" />

      {columns.map((column, index) => {
        const left = leftOffset.get(column.code)
        const right = rightOffset.get(column.code)
        const classes = ['col-head__cell', `col-head__cell--${column.align}`]
        if (column.groupStart) classes.push('col-head__cell--group-start')
        /* У ячейки бренда нет левого отступа (там начинается имя), поэтому и
           заголовок «Название» обязан стоять без него — иначе текст шапки и
           названия брендов идут по двум разным линиям. */
        if (column.role === 'brand') classes.push('col-head__cell--brand')
        if (left !== undefined) classes.push('col-head__cell--sticky-left')
        if (right !== undefined) classes.push('col-head__cell--sticky-right')
        if (column.code === lastLeftCode) classes.push('col-head__cell--edge-left')
        if (column.code === firstRightCode) classes.push('col-head__cell--edge-right')
        if (column.code === myColumn) classes.push('col-head__cell--mine')

        const style = {
          width: `${column.width}px`,
          ...(left !== undefined ? { left: `${left}px` } : null),
          ...(right !== undefined ? { right: `${right}px` } : null),
        }

        if (column.role === 'gutter') {
          return (
            <div key={column.code} className={classes.join(' ')} style={style} role="columnheader"
              aria-colindex={index + 1} aria-label={column.srTitle ?? ''}>
              {selectionEnabled ? (
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={onToggleAll}
                  label={<span className="visually-hidden">{t.row.selectAllShown(shownCount)}</span>}
                />
              ) : null}
            </div>
          )
        }

        const level = sortIndex(column.code)
        const rule = level >= 0 ? sort[level] : undefined
        // Канал подписан в переключателе сверху: дублировать «(ОПТ)» в каждом
        // заголовке — тот самый шум, из-за которого шапка выглядела кашей.
        const channelTitle = false && column.channelLabelled && column.role !== 'groupFold'
          ? t.hint.columnOfChannel(column.title, channelName)
          : null
        // Подпись канала помещается только в широкую колонку; в узкой она
        // живёт в тултипе — обрезанное «Види… (РОЗ…» не читает никто (§4.6).
        const visibleTitle = channelTitle && column.width >= 120
          ? channelTitle
          : (column.title === '' ? (column.srTitle ?? '') : column.title)
        const sortable = column.role !== 'groupFold' && column.source.kind !== 'none'

        const label = (
          <span className="col-head__label t-col">
            {visibleTitle}
            {/* Направление сортировки видно всегда, когда она включена.
                Бледной стрелки-подсказки по наведению больше нет: при
                наведении в заголовке появляется ровно один значок — шеврон
                меню, и он отвечает за всё, включая сортировку. */}
            {rule ? (
              <span className="col-head__sort" aria-hidden="true">
                {rule.direction === 'asc' ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
                {sort.length > 1 ? <sup className="col-head__level">{level + 1}</sup> : null}
              </span>
            ) : null}
          </span>
        )

        /* Нажатие на сам заголовок открывает список сортировки, а не
           переключает её вслепую: «по возрастанию» у приоритета — это
           «сначала TOP-1», и выбирать надо из названных вариантов.
           Shift+клик по-прежнему добавляет уровень сортировки сразу. */
        const inner = sortable ? (
          <button
            type="button"
            className="col-head__btn"
            aria-haspopup="menu"
            aria-sort={rule ? (rule.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
            aria-label={`${column.srTitle ?? column.title}. ${
              rule
                ? rule.direction === 'asc' ? 'Сейчас по возрастанию' : 'Сейчас по убыванию'
                : 'Сортировать'
            }`}
            onClick={(event) => {
              if (event.shiftKey) { onSort(column.code, true); return }
              setMenu({ code: column.code, anchor: event.currentTarget })
            }}
          >
            {label}
          </button>
        ) : label

        const hint = column.code === myColumn
          ? t.hint.myColumn
          : channelTitle ?? column.hint
        if (drag.dragging === column.code) classes.push('col-head__cell--dragging')
        return (
          <div
            key={column.code}
            ref={drag.register(column.code)}
            className={classes.join(' ')}
            style={style}
            role="columnheader"
            aria-colindex={index + 1}
            aria-sort={rule ? (rule.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
            onPointerDown={(event) => drag.onPointerDown(column.code, event)}
          >
            {hint ? <Tooltip content={hint}>{inner}</Tooltip> : inner}
            {/* Ручка ширины: тянем границу заголовка, как в обычной таблице. */}
            <span
              className="col-head__resizer"
              data-no-drag
              role="separator"
              aria-orientation="vertical"
              aria-label={`Ширина колонки «${column.srTitle ?? column.title}»`}
              onPointerDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
                const startX = event.clientX
                const startWidth = column.width
                const target = event.currentTarget
                target.setPointerCapture(event.pointerId)
                const move = (moveEvent: PointerEvent) => {
                  const next = Math.max(72, startWidth + (moveEvent.clientX - startX))
                  onResizeColumn(column.code, next)
                }
                const up = () => {
                  target.removeEventListener('pointermove', move)
                  target.removeEventListener('pointerup', up)
                }
                target.addEventListener('pointermove', move)
                target.addEventListener('pointerup', up)
              }}
            />
            {/* По наведению — шестерёнка: настройки самой колонки. Сортировка
                переехала на нажатие по заголовку, и второй значок для неё
                больше не нужен. */}
            {column.role !== 'groupFold' ? (
              <IconButton
                className="col-head__more"
                data-no-drag
                label={t.columnSettings.openColumn(column.srTitle ?? column.title)}
                icon={<IconSettings size={16} />}
                variant="ghost"
                size="s"
                inTable
                onClick={(event) => setSettings({ code: column.code, anchor: event.currentTarget })}
              />
            ) : null}
          </div>
        )
      })}

      {/* Линия вставки: показывает, куда встанет колонка, на всю высоту
          таблицы — в шапке одного сдвига соседей мало, строки-то не едут. */}
      {drag.dropAt !== null ? (
        <span className="col-head__drop" aria-hidden="true" style={{ left: `${drag.dropAt}px` }} />
      ) : null}

      {settings ? (() => {
        const column = columns.find((item) => item.code === settings.code)
        if (!column) return null
        return (
          <ColumnSettings
            column={column}
            catalog={columns}
            anchor={settings.anchor}
            onClose={() => setSettings(null)}
            onHide={onHide}
          />
        )
      })() : null}

      {menu ? (
        <Menu
          open
          anchor={menu.anchor}
          label={t.columnsMenu.title}
          items={menuItems(menu.code, columns, sort)}
          minWidth={240}
          onClose={() => setMenu(null)}
          onSelect={(id) => {
            const code = menu.code
            setMenu(null)
            if (id === 'sort-asc') onSortDirection(code, 'asc')
            if (id === 'sort-desc') onSortDirection(code, 'desc')
            if (id === 'sort-reset') onSortDirection(code, null)
            if (id === 'hide') onHide(code)
            if (id === 'expand-all') onExpandAll()
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Подписи сортировки по смыслу колонки: «Сначала скрытые» понятнее, чем
 * «по убыванию». Для справочных колонок первым идёт самый значимый конец шкалы.
 */
function sortLabels(code: string): { asc: string; desc: string } {
  switch (code) {
    case 'brand':            return { asc: 'А → Я', desc: 'Я → А' }
    case 'visibility':       return { asc: 'Сначала показываемые', desc: 'Сначала скрытые' }
    case 'tier':             return { asc: 'Сначала A+', desc: 'Сначала C' }
    case 'seoPriority':      return { asc: 'Сначала TOP-1', desc: 'Сначала «Не продвигать»' }
    case 'contextPriority':  return { asc: 'Сначала TOP-1', desc: 'Сначала TOP-3 товарка' }
    case 'url':              return { asc: 'Сначала с адресом', desc: 'Сначала без адреса' }
    case 'categoriesCount':  return { asc: 'Сначала с малым числом категорий', desc: 'Сначала с большим числом' }
    case 'missing':          return { asc: 'Сначала заполненные', desc: 'Сначала с пропусками' }
    case 'stockDisplay':
    case 'oos':              return { asc: 'Сначала быстрые сроки', desc: 'Сначаладлинные сроки' }
    default:                 return { asc: 'По возрастанию', desc: 'По убыванию' }
  }
}

function menuItems(
  code: string, columns: ColumnDef[], sort: readonly SortRule[],
): MenuItem[] {
  const column = columns.find((item) => item.code === code)
  const current = sort.find((rule) => rule.column === code)
  const labels = sortLabels(code)
  const items: MenuItem[] = []

  const sortable = column && column.role !== 'groupFold' && column.source.kind !== 'none'
  if (sortable) {
    items.push({ id: 'sort-asc', label: labels.asc, ...(current?.direction === 'asc' ? { icon: <IconCheck size={16} /> } : null) })
    items.push({ id: 'sort-desc', label: labels.desc, ...(current?.direction === 'desc' ? { icon: <IconCheck size={16} /> } : null) })
    if (current) items.push({ id: 'sort-reset', label: 'Сбросить сортировку' })
  }

  if (code === 'brand') {
    items.push({ id: 'expand-all', label: t.row.expandAllLabel, separatorBefore: items.length > 0 })
    return items
  }

  /* «Скрыть колонку» — с глазиком: тот самый значок, который раньше висел
     в самом заголовке и отнимал место у названия. */
  items.push({
    id: 'hide',
    label: t.columnsMenu.hide,
    icon: <IconEyeOff size={16} />,
    separatorBefore: items.length > 0,
    disabled: column ? !column.optional : true,
    ...(column && !column.optional ? { reason: t.columnsMenu.required } : null),
  })
  return items
}
